import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

/**
 * Reusable Turnstile widget for public preregistration forms.
 * Managed mode; Cloudflare decides when to show a challenge.
 *
 * Props:
 *   - onToken(token: string): fires when a fresh token is issued
 *   - onExpire(): fires when token expires (single-use / TTL)
 *   - onError(): fires on widget error
 *
 * Ref methods:
 *   - reset(): forces widget to issue a fresh token
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is missing, renders nothing (dev-mode).
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  { onToken, onExpire, onError, testId = "turnstile-widget" },
  ref
) {
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  const innerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      try { innerRef.current?.reset?.(); } catch { /* ignore */ }
    },
  }));

  if (!siteKey) {
    // Dev fallback — backend also bypasses verification when secret is empty.
    return null;
  }

  return (
    <div data-testid={testId} className="mt-1">
      <Turnstile
        ref={innerRef}
        siteKey={siteKey}
        options={{ theme: "dark", size: "flexible" }}
        onSuccess={(t) => onToken?.(t)}
        onExpire={() => { onExpire?.(); onToken?.(""); }}
        onError={() => { onError?.(); onToken?.(""); }}
      />
    </div>
  );
});

export default TurnstileWidget;
